/**
 * Start Process Actor
 *
 * XState actor that handles the full process initiation lifecycle:
 * 1. Optionally saves import data to backend (if data is provided)
 * 2. Calls the appropriate backend process API (set-companies, set-tags, etc.)
 *
 * After this actor completes, the machine transitions to polling for completion.
 * This consolidates the save + API call pattern that was previously split
 * between the component and machine.
 */
import { fromPromise } from 'xstate'
import { updateImportPreview } from '../../importer-api'
import {
  performSetCompanies,
  performSetTags,
  performSetDeals,
  performSetContacts,
  performSetCompanyNotes,
} from '../../importer-api'
import { ImportData, ImportDataRowsFormat } from '../../importer-types'
import { ProcessType } from '../types'

export interface StartProcessInput {
  importNumber: number
  processType: ProcessType | null
  data: ImportData | null
}

/**
 * Convert ImportData (UI format) to ImportDataRowsFormat (API format)
 */
function toRowsJSON(data: ImportData): ImportDataRowsFormat {
  if (!data.rows.length) {
    return {
      headers: [],
      rows: {
        columns: [],
        rows: [],
      },
    }
  }
  const columns = Object.keys(data.rows[0])
  const rows = data.rows.map((obj) => columns.map((key) => obj[key]))
  return {
    headers: data.headers,
    rows: {
      columns,
      rows,
    },
  }
}

/**
 * Call the appropriate backend API based on process type.
 */
async function callProcessAPI(
  importNumber: number,
  processType: ProcessType
): Promise<void> {
  switch (processType) {
    case ProcessType.SETTING_COMPANIES:
      await performSetCompanies(importNumber)
      break
    case ProcessType.SETTING_TAGS:
      await performSetTags(importNumber)
      break
    case ProcessType.SETTING_DEALS:
      await performSetDeals(importNumber)
      break
    case ProcessType.SETTING_CONTACTS:
      await performSetContacts(importNumber)
      break
    case ProcessType.SETTING_COMPANY_NOTES:
      await performSetCompanyNotes(importNumber)
      break
    default:
      throw new Error(`Unknown process type: ${processType}`)
  }
}

export const startProcessActor = fromPromise<void, StartProcessInput>(
  async ({ input }) => {
    if (!input.processType) {
      throw new Error('Process type is required')
    }

    // 1. Save data if provided
    try {
      if (input.data) {
        const jsonData = toRowsJSON(input.data)
        await updateImportPreview(input.importNumber, {
          import_data: { data: jsonData },
        })
      }
    } catch (error) {
      throw new Error(
        `Failed to save import data: ${error instanceof Error ? error.message : String(error)}`,
        { cause: error }
      )
    }

    // 2. Call the appropriate process API
    try {
      await callProcessAPI(input.importNumber, input.processType)
    } catch (error) {
      throw new Error(
        `Failed to start ${input.processType}: ${error instanceof Error ? error.message : String(error)}`,
        { cause: error }
      )
    }
  }
)
